/**
 * Customer Account API — pure fetch functions.
 *
 * No React, no hooks, no TanStack Query — just typed wrappers over the
 * FastAPI endpoints. The React layer (hooks.ts) wraps these with useQuery /
 * useMutation.
 *
 * Endpoint contract from backend/routers/customer_account.py:
 *   GET    /api/customer-account/draft   → DraftResponse | 204
 *   PUT    /api/customer-account/draft   → DraftResponse (creates / amends)
 *   DELETE /api/customer-account/draft   → 204
 *   POST   /api/customer-account/submit  → CustomerSubmissionResponse
 */
import { apiCall } from '@/lib/api-client';
import type { CustomerAccountValues } from '@/schemas/customer-account';

/** Status from backend Customer.status column. */
export type CustomerStatus = 'draft' | 'submitted' | 'archived';

/** Shape returned by GET /draft and PUT /draft. */
export interface DraftResponse {
  data: Partial<CustomerAccountValues> & Record<string, unknown>;
  status: CustomerStatus;
  created_at: string;
  updated_at: string;
}

/** Shape returned by POST /submit. */
export interface SubmissionResponse extends DraftResponse {
  submitted_at: string | null;
}

const ENDPOINT = '/api/customer-account';

/**
 * GET the current user's draft (or submitted customer in amend mode).
 * Returns `null` when the user has no draft yet (backend 204 No Content).
 */
export async function getCustomerDraft(): Promise<DraftResponse | null> {
  const res = await apiCall<DraftResponse | null>(`${ENDPOINT}/draft`, {
    method: 'GET',
    allow204: true,
  });
  return res;
}

/** PUT (create / overwrite / amend) the user's draft. */
export async function upsertCustomerDraft(
  values: Partial<CustomerAccountValues>,
): Promise<DraftResponse> {
  return apiCall<DraftResponse>(`${ENDPOINT}/draft`, {
    method: 'PUT',
    json: { data: values },
  });
}

/** DELETE the user's draft (returns void). */
export async function deleteCustomerDraft(): Promise<void> {
  await apiCall<null>(`${ENDPOINT}/draft`, {
    method: 'DELETE',
    allow204: true,
  });
}

/** POST /submit — finalize the draft. Throws ApiConflictError if already submitted. */
export async function submitCustomerAccount(): Promise<SubmissionResponse> {
  return apiCall<SubmissionResponse>(`${ENDPOINT}/submit`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Companies (multi-company account)
//
// Endpoint contract from backend/routers/companies.py:
//   GET    /api/companies                              → { companies: CompanyDto[] }
//   POST   /api/companies                              → CompanyDto (201)
//   GET    /api/companies/{id}                         → CompanyDto
//   PUT    /api/companies/{id}                         → CompanyDto
//   POST   /api/companies/{id}/submit                  → CompanyDto
//   DELETE /api/companies/{id}                         → 204
//   PUT    /api/companies/{id}/jurisdictions/{market}  → JurisdictionDto
//   DELETE /api/companies/{id}/jurisdictions/{market}  → 204
// ---------------------------------------------------------------------------

/** One per-market row under a company. */
export interface JurisdictionDto {
  market_code: string;
  status: CustomerStatus;
  data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/** A legal entity under the account. */
export interface CompanyDto {
  id: number;
  customer_id: number;
  parent_company_id: number | null;
  is_primary: boolean;
  base: Partial<CustomerAccountValues> & Record<string, unknown>;
  status: CustomerStatus;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
  jurisdictions: JurisdictionDto[];
}

const COMPANIES = '/api/companies';

/** List every company in the user's account (with their jurisdictions). */
export async function listCompanies(): Promise<CompanyDto[]> {
  const res = await apiCall<{ companies: CompanyDto[] }>(COMPANIES, { method: 'GET' });
  return res.companies;
}

/** Create a company. The first company in an account becomes the primary. */
export async function createCompany(
  base: Partial<CustomerAccountValues>,
  parentCompanyId: number | null = null,
): Promise<CompanyDto> {
  return apiCall<CompanyDto>(COMPANIES, {
    method: 'POST',
    json: { base, parent_company_id: parentCompanyId },
  });
}

/** Fetch one company + its jurisdictions. */
export async function getCompany(id: number): Promise<CompanyDto> {
  return apiCall<CompanyDto>(`${COMPANIES}/${id}`, { method: 'GET' });
}

/** Overwrite a company's base (+ optional parent; cycle-guarded server-side). */
export async function updateCompany(
  id: number,
  base: Partial<CustomerAccountValues>,
  parentCompanyId: number | null = null,
): Promise<CompanyDto> {
  return apiCall<CompanyDto>(`${COMPANIES}/${id}`, {
    method: 'PUT',
    json: { base, parent_company_id: parentCompanyId },
  });
}

/** Finalize a company (draft → submitted). Throws ApiConflictError if already submitted. */
export async function submitCompany(id: number): Promise<CompanyDto> {
  return apiCall<CompanyDto>(`${COMPANIES}/${id}/submit`, { method: 'POST' });
}

/** Delete a company (and its jurisdictions). Promotes a new primary server-side if needed. */
export async function deleteCompany(id: number): Promise<void> {
  await apiCall<null>(`${COMPANIES}/${id}`, { method: 'DELETE', allow204: true });
}

/** Create or overwrite a company's market row (US/EU/DE/…). */
export async function upsertJurisdiction(
  companyId: number,
  marketCode: string,
  data: Record<string, unknown>,
): Promise<JurisdictionDto> {
  return apiCall<JurisdictionDto>(
    `${COMPANIES}/${companyId}/jurisdictions/${marketCode}`,
    { method: 'PUT', json: { data } },
  );
}

/** Remove a company's market row. */
export async function deleteJurisdiction(
  companyId: number,
  marketCode: string,
): Promise<void> {
  await apiCall<null>(`${COMPANIES}/${companyId}/jurisdictions/${marketCode}`, {
    method: 'DELETE',
    allow204: true,
  });
}

// ── Supporting documents (Section 12 — multipart upload + PDF→JSON) ──────────

export interface CompanyDocumentDto {
  id: number;
  doc_type: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  page_count: number | null;
  char_count: number;
  text_extracted: boolean;
}

export async function listCompanyDocuments(
  companyId: number,
): Promise<CompanyDocumentDto[]> {
  return apiCall<CompanyDocumentDto[]>(`${COMPANIES}/${companyId}/documents`, {
    method: 'GET',
  });
}

export async function uploadCompanyDocument(
  companyId: number,
  docType: string,
  file: File,
): Promise<CompanyDocumentDto> {
  const form = new FormData();
  form.append('file', file);
  return apiCall<CompanyDocumentDto>(
    `${COMPANIES}/${companyId}/documents/${encodeURIComponent(docType)}`,
    { method: 'POST', bodyRaw: form },
  );
}

export async function deleteCompanyDocument(
  companyId: number,
  attachmentId: number,
): Promise<void> {
  await apiCall<null>(`${COMPANIES}/${companyId}/documents/${attachmentId}`, {
    method: 'DELETE',
    allow204: true,
  });
}

/** Direct download URL (the binary) — used in an anchor href. */
export function companyDocumentDownloadUrl(
  companyId: number,
  attachmentId: number,
): string {
  return `${COMPANIES}/${companyId}/documents/${attachmentId}/download`;
}
